import React, { useMemo, useState, type JSX } from "react";
import { ArrowDownTrayIcon, ClipboardDocumentIcon, CheckIcon } from "@heroicons/react/24/outline";
import type { Deal, Document } from "./types/redux";
import { buildDealColumns, type ColumnDef } from "./InlineDealsColumns";

interface InlineDealsTableProps {
  deals: Deal[];
  documents?: Document[];
  onDealClick: (deal: Deal) => void;
}

const MAX_VISIBLE_ROWS = 10;

/** Build a delimited matrix (header row first) from the current column set */
function buildExportRows(columns: ColumnDef[], deals: Deal[]): string[][] {
  const headers = columns.map((c) => c.header);
  const rows = deals.map((d) => columns.map((c) => c.exportValue(d)));
  return [headers, ...rows];
}

function downloadCsv(columns: ColumnDef[], deals: Deal[]): void {
  const escape = (v: string): string => `"${v.replace(/"/g, '""')}"`;
  const csv = buildExportRows(columns, deals)
    .map((r) => r.map(escape).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "deals.csv";
  link.click();
  URL.revokeObjectURL(url);
}

async function writeTsvToClipboard(columns: ColumnDef[], deals: Deal[]): Promise<void> {
  const tsv = buildExportRows(columns, deals)
    .map((r) => r.join("\t"))
    .join("\n");
  try {
    await navigator.clipboard.writeText(tsv);
  } catch {
    // Fallback for older browsers
    const textarea = document.createElement("textarea");
    textarea.value = tsv;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }
}

function TableActions({
  columns,
  deals,
}: {
  columns: ColumnDef[];
  deals: Deal[];
}): JSX.Element {
  const [copied, setCopied] = useState<boolean>(false);

  const handleCopy = async (): Promise<void> => {
    await writeTsvToClipboard(columns, deals);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="flex items-center gap-3 px-3 py-2 bg-cvc-tableHeader border-t border-cvc-tableBorder">
      <button
        onClick={(e) => {
          e.stopPropagation();
          downloadCsv(columns, deals);
        }}
        className="inline-flex items-center gap-1.5 text-xs text-cvc-headerText hover:text-cvc-blue transition-colors"
      >
        <ArrowDownTrayIcon className="w-3.5 h-3.5" />
        Export to Excel
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          void handleCopy();
        }}
        className="inline-flex items-center gap-1.5 text-xs text-cvc-headerText hover:text-cvc-blue transition-colors"
        aria-live="polite"
      >
        {copied ? (
          <>
            <CheckIcon className="w-3.5 h-3.5 text-cvc-green" />
            <span className="text-cvc-green">Copied</span>
          </>
        ) : (
          <>
            <ClipboardDocumentIcon className="w-3.5 h-3.5" />
            Copy table
          </>
        )}
      </button>
      {deals.length > MAX_VISIBLE_ROWS && (
        <span className="ml-auto text-xs text-cvc-headerText">
          Showing {MAX_VISIBLE_ROWS} of {deals.length} — scroll for more
        </span>
      )}
    </div>
  );
}

function DealRow({
  deal,
  columns,
  onDealClick,
}: {
  deal: Deal;
  columns: ColumnDef[];
  onDealClick: (deal: Deal) => void;
}): JSX.Element {
  return (
    <tr
      className="hover:bg-cvc-lb2 transition-colors duration-150 cursor-pointer"
      onClick={() => onDealClick(deal)}
    >
      {columns.map((col) => (
        <td
          key={col.key}
          className={`py-2 px-3 text-sm text-cvc-text ${col.cellClassName || ""} ${col.className || ""}`}
        >
          {col.render(deal)}
        </td>
      ))}
    </tr>
  );
}

export default function InlineDealsTable_Component({
  deals,
  documents,
  onDealClick,
}: InlineDealsTableProps): JSX.Element | null {
  const columns = useMemo<ColumnDef[]>(() => buildDealColumns(documents), [documents]);

  if (deals.length === 0) return null;

  return (
    <div className="mt-3 mb-1 border border-cvc-tableBorder rounded-[5px] overflow-hidden">
      {/* Cap visible rows at ~10; thead stays sticky while body scrolls. */}
      <div className="overflow-auto max-h-[440px]">
        <table className="min-w-full divide-y divide-cvc-tableBorder">
          <thead className="bg-cvc-tableHeader sticky top-0 z-10">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={`py-2 px-3 text-left text-xs font-cvcBody font-normal text-cvc-headerText uppercase tracking-wider ${col.className || ""}`}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-cvc-tableBorder font-cvcBody">
            {deals.map((deal, idx) => (
              <DealRow
                key={deal.dealId || deal.id || idx}
                deal={deal}
                columns={columns}
                onDealClick={onDealClick}
              />
            ))}
          </tbody>
        </table>
      </div>
      <TableActions columns={columns} deals={deals} />
    </div>
  );
}
